import {
  ArrowLeftIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { BookOpenIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { PauseIcon } from '@heroicons/react/16/solid';
import classnames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Dropdown, Loading, Tooltip } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { randomString } from '../../../../functions/string.functions.ts';
import { showStatusEmoji } from '../../../../pages/MessengerV2/components/LeadRow/LeadRow.function.ts';
import { useIntegrationsContext } from '../../../../providers/IntegrationsProvider/Integrations.context.ts';
import { useModalsContext } from '../../../../providers/ModalsProvider/Modals.context.ts';
import { integrationIcons, integrationNames } from 'src/constants/channel.constants';
import { useSocketsContext } from '../../../../providers/SocketsProvider/Sockets.context.ts';
import { eChannelType } from '../../../../providers/SocketsProvider/Sockets.enums.ts';
import { useUserContext } from '../../../../providers/UserProvider/User.context.ts';
import { eUserRole } from '../../../../providers/UserProvider/User.enums.ts';
import { GetInitialsName } from '../../../../utils/GetInitialsName.ts';
import { tAdvanceSearchLead } from '../../../AdvanceSearch/AdvanceSearchModal.types.ts';
import useChatActions from '../../Chat.actions.ts';
import { ChatMessageContainerEnum } from '../../Chat.enum.ts';
import { chatLeadParser } from '../../Chat.functions.ts';
import { useChatContext } from '../../Chat.hook.ts';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';
import { tLeadInfoProps, tLeadUsers } from '../../Chat.types.ts';
import {
  canEditAssignment,
  getAssignableMenuUsers,
  isLeadAssigned,
  resolveAssigneeName,
  shouldShowAssignToMe,
} from './LeadInfo.functions.ts';
import './LeadInfo.styles.scss';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook.ts';
import { useFeatureFlagsContext } from '../../../../providers/FeatureFlagsProvider/FeatureFlags.context';

export default function LeadInfo(props: tLeadInfoProps) {
  const {
    setSelectedMessageContainer,
    selectedMessageContainer,
    selectedChat,
    loading,
    autoRespondEnabled,
    leadAgentPaused,
  } = useChatContext();
  const { isSmartLeadBotEnabled } = useFeatureFlagsContext();
  const {
    selectedLeadForChatModal,
    setSelectedLeadForChatModal,
    maximizeModal,
    setMaximizeModal,
  } = useModalsContext();
  const { updateLead } = useChatActions();
  const { currentUserId, currentUserRole } = useUserContext();
  const { currentFunnel, removeLeadFromFunnel, updateLeadAssignee } = useBoardSlice();
  const { leadUpdatedEvent } = useSocketsContext();
  const { hasWaBusinessIntegration } = useIntegrationsContext();
  const { canUserAccessWhatsAppApi } = useACLContext();
  const defaultChannel = canUserAccessWhatsAppApi()
    ? eChannelType.WHATSAPP_API
    : eChannelType.WHATSAPP_WEB;

  const integrationDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownUserRef = useRef<HTMLDivElement>(null);
  const [assignedLoading, setAssigneeLoading] = useState(false);
  const [isDropdownAssigneOpen, setIsDropdownAssigneOpen] =
    useState<boolean>(false);
  const [isIntegrationDropdownOpen, setIsIntegrationDropdownOpen] =
    useState(false);
  const { t } = useTranslation('chat/chat');

  const [selectedLead, setSelectedLead] = useState<tAdvanceSearchLead | null>(
    null,
  );

  const [avatarImage, setAvatarImage] = useState<string | undefined>(
    selectedLead?.profilePicture || undefined,
  );

  useEffect(() => {
    setSelectedLead(selectedLeadForChatModal ?? null);
  }, [selectedLeadForChatModal]);

  useEffect(() => {
    setAvatarImage(selectedLead?.profilePicture || undefined);
  }, [selectedLead?.profilePicture]);

  // RET-1208 (P2-5): close the assignment dropdown whenever the lead's
  // owner changes — covers the race where a teammate's claim arrives via
  // websocket while a SELLER has the menu open on the same (then-unassigned)
  // lead. Without this, a stale "Asignarme este lead" row would still be
  // clickable and would silently reassign the now-other-owned lead.
  useEffect(() => {
    setIsDropdownAssigneOpen(false);
  }, [selectedLead?.assignedUser?.authID]);

  leadUpdatedEvent.useSubscription((lead) => {
    if (selectedLead?.id !== lead.response?.uuid) {
      return;
    }
    setSelectedLead((oldLead) => {
      if (!oldLead) return null;
      return chatLeadParser(lead, oldLead);
    });
  });

  const leadUsers = selectedChat?.lead?.users ?? [];

  const currentIntegration =
    selectedLead?.chat?.integration || eChannelType.WHATSAPP_WEB;

  const alternativeIntegration =
    currentIntegration === eChannelType.WHATSAPP_API
      ? eChannelType.WHATSAPP_WEB
      : eChannelType.WHATSAPP_API;

  const alternativeIntegrationName =
    alternativeIntegration === eChannelType.WHATSAPP_API
      ? 'WhatsApp Business'
      : 'WhatsApp Web';

  const getSelectedIntegration = () =>
    integrationIcons[(selectedChat?.integration ?? defaultChannel) as eChannelType] ?? '/images/logo/logo-sm.png';

  const getSelectedIntegrationName = () =>
    integrationNames[selectedLead?.chat?.integration as eChannelType] ?? 'LS';

  useEffect(() => {
    const handleClickOutsideIntegration = (event: MouseEvent) => {
      if (
        integrationDropdownRef.current &&
        !integrationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsIntegrationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideIntegration);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideIntegration);
    };
  }, []);

  function toggleDropdown(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    setIsDropdownAssigneOpen(!isDropdownAssigneOpen);
  }

  async function assignUser(user: tLeadUsers | null) {
    const leadId = selectedLeadForChatModal?.id;
    setAssigneeLoading(true);
    const newValues = user
      ? { user_uuid: user.auth_id }
      : { user_uuid: null };

    const succeeded = await updateLead(newValues, { leadId });

    if (!succeeded) {
      // updateLead surfaced its own error toast; don't mutate local
      // state or strip the lead from the funnel — those would diverge
      // from the backend and force a refresh to recover.
      setAssigneeLoading(false);
      return;
    }

    // Optimistic update of the canonical `assignedUser` shape that
    // every render gate and label in this file reads. Previously this
    // wrote to an orphan `assigneeAuthId` key not on tAdvanceSearchLead,
    // leaving the UI stuck in the pre-click state until the websocket
    // arrived — masking the SELLER self-claim affordance (RET-1208).
    const withAssignee = (prevLead: tAdvanceSearchLead | null) => {
      if (!prevLead || prevLead.id !== leadId) return prevLead;
      return {
        ...prevLead,
        assignedUser: user
          ? { authID: user.auth_id, name: user.name }
          : undefined,
      };
    };
    setSelectedLead(withAssignee);
    setSelectedLeadForChatModal(withAssignee);

    if (leadId) updateLeadAssignee(leadId, user ? { authID: user.auth_id, name: user.name } : null);

    const isSeller = currentUserRole === eUserRole.SELLER;
    const funnelIsPrivate = currentFunnel != null && !currentFunnel.isPublic;
    const reassignedToSomeoneElse = user?.auth_id !== currentUserId;

    if (isSeller && funnelIsPrivate && reassignedToSomeoneElse && leadId) {
      removeLeadFromFunnel(leadId);
    }

    setAssigneeLoading(false);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownUserRef.current &&
        !dropdownUserRef.current.contains(event.target as Node)
      ) {
        setIsDropdownAssigneOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const leadAgentChipLabel = t(
    leadAgentPaused.paused
      ? 'smartLeadbot.leadAgentChip.paused'
      : 'smartLeadbot.leadAgentChip.active',
  );

  return (
    <div className="LeadInfo">
      <div className="LeadInfo__Container">
        <div className="LeadInfo__Avatar">
          <Avatar
            src={avatarImage}
            shape={'circle'}
            className="skeleton"
            size="xs"
            letters={
              loading ? undefined : GetInitialsName(selectedLead?.name ?? 'LS')
            }
            color={'primary'}
            onError={() => {
              setAvatarImage(undefined);
            }}
          />
          <img
            src={getSelectedIntegration()}
            alt="integration"
            className="LeadInfo__Avatar__IntegrationIcon"
          />
        </div>

        <div className="LeadInfo__Info">
          <div className="LeadInfo__Info__Name">
            <p>
              {selectedLead?.name?.includes('@lid')
                ? t('leadConfidential')
                : selectedLead?.name}
            </p>
            {selectedLead?.handle?.includes('@lid') ? (
              <Tooltip
                message={t('leadPrivacyNumber')}
                position="right"
                color="primary"
              >
                <InformationCircleIcon className="w-5 h-5 text-primary" />
              </Tooltip>
            ) : null}
            {showStatusEmoji(selectedLead?.status?.name ?? 'NEUTRAL')}
            {isSmartLeadBotEnabled && autoRespondEnabled && (
              <span
                className={classnames(
                  'inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5',
                  leadAgentPaused.paused
                    ? 'bg-[var(--ls-accent-neutral-lighter)] border-[var(--ls-border-neutral-light)] text-[var(--ls-text-neutral-light)]'
                    : 'bg-primary/10 border-primary/25 text-primary',
                )}
                title={leadAgentChipLabel}
                aria-label={leadAgentChipLabel}
              >
                {/* Paused shows the in-chat paused banner's icon, so the two
                    read as the same state. */}
                {leadAgentPaused.paused ? (
                  <PauseIcon className="w-3 h-3 shrink-0" />
                ) : (
                  <SparklesIcon className="w-3 h-3 shrink-0" />
                )}
                <span className="text-[9px] font-bold leading-none tracking-wide uppercase">IA</span>
              </span>
            )}
          </div>

          {/* RET-1208: Unified assignment control. See LeadInfo.functions.ts
             `canEditAssignment` for the full decision matrix. */}
          {canEditAssignment(
            currentUserRole,
            currentUserId,
            selectedLead?.assignedUser,
          ) ? (
            <Dropdown
              className="LeadInfo__Info__Dropdown"
              vertical="bottom"
              horizontal="right"
              open={isDropdownAssigneOpen}
              ref={dropdownUserRef}
            >
              <Dropdown.Toggle
                size="sm"
                color="ghost"
                className="w-full"
                onClick={toggleDropdown}
              >
                {assignedLoading ? (
                  <Loading color="primary" size="xs" />
                ) : (
                  <UserIcon className="LeadInfo__Info__Dropdown__Icon" />
                )}
                {resolveAssigneeName(selectedLead?.assignedUser, leadUsers)
                  ?? t('leadInfo.notAssigned')}
                {isDropdownAssigneOpen ? (
                  <ChevronUpIcon className="LeadInfo__Info__Dropdown__Icon" />
                ) : (
                  <ChevronDownIcon className="LeadInfo__Info__Dropdown__Icon" />
                )}
              </Dropdown.Toggle>
              {isDropdownAssigneOpen && (
                <Dropdown.Menu className="w-52 z-10">
                  {getAssignableMenuUsers(
                    leadUsers,
                    selectedLead?.assignedUser,
                  ).map((user) => {
                    const showAssignToMe = shouldShowAssignToMe(
                      user,
                      currentUserId,
                      currentUserRole,
                      selectedLead?.assignedUser,
                    );

                    return (
                      <Dropdown.Item
                        key={randomString()}
                        onClick={(event) => {
                          assignUser(user);
                          toggleDropdown(event);
                        }}
                      >
                        {showAssignToMe
                          ? t('leadInfo.assignToMe')
                          : user.name}
                      </Dropdown.Item>
                    );
                  })}
                  {isLeadAssigned(selectedLead?.assignedUser) ? (
                    <Dropdown.Item
                      key={randomString()}
                      onClick={(event) => {
                        assignUser(null);
                        toggleDropdown(event);
                      }}
                    >
                      {t('leadInfo.notAssigned')}
                    </Dropdown.Item>
                  ) : null}
                </Dropdown.Menu>
              )}
            </Dropdown>
          ) : (
            <div className="LeadInfo__Info__AssigneeReadOnly">
              <UserIcon className="LeadInfo__Info__Dropdown__Icon" />
              <span>
                {(() => {
                  const assigneeName = resolveAssigneeName(
                    selectedLead?.assignedUser,
                    leadUsers,
                  );
                  // RET-1208 (P2-1): when the assignee is not resolvable
                  // (former teammate, stale `selectedChat.lead.users`),
                  // fall back to the bare "Asignado" label instead of
                  // rendering "Asignado a: " with a dangling colon.
                  return assigneeName
                    ? t('leadInfo.assignedTo', { name: assigneeName })
                    : t('leadInfo.assigned');
                })()}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="LeadInfo__Container">
        {selectedMessageContainer === ChatMessageContainerEnum.CHAT ? (
          <Button
            size="sm"
            variant="outline"
            color="ghost"
            onClick={() =>
              setSelectedMessageContainer(ChatMessageContainerEnum.RECORD)
            }
          >
            <BookOpenIcon className="btn-icon" />
            {t('leadInfo.history')}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setSelectedMessageContainer(ChatMessageContainerEnum.CHAT)
            }
          >
            <div className="LeadInfo__Container__Button">
              <ArrowLeftIcon className="LeadInfo__Container__Button___Icon" />
              {t('leadInfo.backToChat')}
            </div>
          </Button>
        )}

        <div className="LeadInfo__IntegrationSection">
          <Tooltip message={getSelectedIntegrationName()} position="bottom">
            <div style={{ position: 'relative' }}>
              <Button
                shape="circle"
                size="sm"
                color="ghost"
                onClick={() =>
                  setIsIntegrationDropdownOpen(!isIntegrationDropdownOpen)
                }
              >
                <img src={getSelectedIntegration()} alt="integration" />
              </Button>
              {isIntegrationDropdownOpen &&
                selectedChat?.channels &&
                (selectedChat?.integration === eChannelType.WHATSAPP_API ||
                  selectedChat?.integration === eChannelType.WHATSAPP_WEB) &&
                hasWaBusinessIntegration() && (
                  <div
                    ref={integrationDropdownRef}
                    className="LeadInfo__IntegrationDropdown"
                  >
                    {
                      <Dropdown.Menu className="w-52">
                        <Dropdown.Item
                          onClick={() => {
                            setSelectedLeadForChatModal(
                              (prevValue) =>
                                ({
                                  ...prevValue,
                                  chat: {
                                    ...prevValue?.chat,
                                    integration: alternativeIntegration,
                                  },
                                }) as tAdvanceSearchLead,
                            );
                            setIsIntegrationDropdownOpen(false);
                          }}
                        >
                          {alternativeIntegrationName}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    }
                  </div>
                )}
            </div>
          </Tooltip>
        </div>

        {props.isChatModal && (
          <>
            <Tooltip
              message={
                maximizeModal ? t('leadInfo.collapse') : t('leadInfo.expand')
              }
              position="bottom"
            >
              <Button
                shape="circle"
                size="sm"
                color="ghost"
                onClick={() => {
                  setMaximizeModal(!maximizeModal);
                }}
              >
                {maximizeModal ? (
                  <ArrowsPointingInIcon />
                ) : (
                  <ArrowsPointingOutIcon />
                )}
              </Button>
            </Tooltip>

            <Tooltip message={t('leadInfo.close')} position="bottom">
              <Button
                shape="circle"
                size="sm"
                color="ghost"
                onClick={() => {
                  if (props.onCloseModal) {
                    props.onCloseModal();
                  }
                }}
              >
                <XMarkIcon />
              </Button>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}
